import { Injectable } from '@angular/core';
import { collection, doc, documentId, getDoc, getDocs, limit, orderBy, query, setDoc, Timestamp, where, writeBatch } from 'firebase/firestore';
import { FirebaseService } from './firebase.service';

export type MarketLevelPeriod = 'daily' | 'weekly';
const TICKER_ORDER = ['NQ', 'ES', 'RTY', 'GC', 'QQQ', 'SPY', 'IWM'];

export interface MarketLevelRow {
  ticker: string;
  close: number;
  iv: number;
  expectedMove: number;
  s1: number;
  pivot: number;
  r1: number;
  s2: number;
  r2: number;
}

export interface MarketLevelSnapshot {
  period: MarketLevelPeriod;
  date: string;
  rows: MarketLevelRow[];
  source?: string;
  updatedBy: string | null;
  updatedAt: Timestamp;
}

export type MarketLevelDisplayRow = MarketLevelRow & { date: string; source?: string; updatedAt: Date | null };

@Injectable({ providedIn: 'root' })
export class MarketLevelsService {
  private db: FirebaseService['db'];

  constructor(firebase: FirebaseService) {
    this.db = firebase.db;
  }

  async saveSnapshot(period: MarketLevelPeriod, date: string, rows: MarketLevelRow[], updatedBy: string | null, source?: string): Promise<void> {
    const updatedAt = Timestamp.now();
    const batchId = source ? `${period}_${source}_${date}` : `${period}_${date}`;
    const sourceFields = source ? { source } : {};
    const snapshot: MarketLevelSnapshot = { period, date, rows, updatedBy, updatedAt, ...sourceFields };

    await setDoc(doc(this.db, 'marketLevels', batchId), snapshot);

    const batch = writeBatch(this.db);
    const tickerHistoryCollection = period === 'daily' ? 'marketTickerLevelsDaily' : 'marketTickerLevelsWeekly';
    for (const row of rows) {
      const latestId = source ? `${period}_${source}_${row.ticker}` : `${period}_${row.ticker}`;
      const recordId = source ? `${source}_${date}` : date;
      batch.set(doc(this.db, 'marketLevelsLatest', latestId), {
        ...row,
        period,
        date,
        updatedBy,
        updatedAt,
        ...sourceFields,
      });
      batch.set(doc(this.db, tickerHistoryCollection, row.ticker, 'records', recordId), {
        ...row,
        period,
        date,
        updatedBy,
        updatedAt,
        ...sourceFields,
      });
    }
    await batch.commit();
  }

  async loadLatest(period: MarketLevelPeriod, source?: string): Promise<MarketLevelDisplayRow[]> {
    const constraints = source
      ? [where('period', '==', period), where('source', '==', source)]
      : [where('period', '==', period)];
    const q = query(collection(this.db, 'marketLevelsLatest'), ...constraints);
    const snap = await getDocs(q);
    return snap.docs
      .map(docSnap => {
        const data = docSnap.data();
        return {
          ticker: data['ticker'],
          close: data['close'] ?? 0,
          iv: data['iv'] ?? 0,
          expectedMove: data['expectedMove'] ?? 0,
          s1: data['s1'],
          pivot: data['pivot'],
          r1: data['r1'],
          s2: data['s2'],
          r2: data['r2'],
          date: data['date'],
          source: data['source'],
          updatedAt: this.toDate(data['updatedAt']),
        };
      })
      .filter(row => row.ticker && row.date && (source || !row.source))
      .sort((a, b) => this.tickerOrder(a.ticker) - this.tickerOrder(b.ticker));
  }

  async loadLatestByTickers(period: MarketLevelPeriod, tickers: string[], source?: string): Promise<MarketLevelDisplayRow[]> {
    const docs = await Promise.all(
      tickers.map(ticker => {
        const normalizedTicker = ticker.trim().toUpperCase();
        const latestId = source ? `${period}_${source}_${normalizedTicker}` : `${period}_${normalizedTicker}`;
        return getDoc(doc(this.db, 'marketLevelsLatest', latestId));
      })
    );

    return docs
      .filter(docSnap => docSnap.exists())
      .map(docSnap => this.toDisplayRow(docSnap.data(), docSnap.data()['ticker'], docSnap.data()['date']))
      .filter(row => row.ticker && row.date)
      .sort((a, b) => this.tickerOrder(a.ticker) - this.tickerOrder(b.ticker));
  }

  /**
   * The record just before the latest one, per ticker — e.g. "previous week"
   * for period 'weekly'. Deliberately just "one record back" rather than
   * "today's date minus 7 days": if a holiday pushed the prior week's entry
   * to a different day, this still finds it instead of coming back empty.
   */
  async loadPreviousByTickers(period: MarketLevelPeriod, tickers: string[], source?: string): Promise<MarketLevelDisplayRow[]> {
    const rows = await Promise.all(
      tickers.map(async ticker => {
        const history = await this.loadTickerHistory(period, ticker, [], 2, source);
        return history[1] ?? null;
      })
    );
    return rows
      .filter((row): row is MarketLevelDisplayRow => row != null)
      .sort((a, b) => this.tickerOrder(a.ticker) - this.tickerOrder(b.ticker));
  }

  async loadTickerHistory(period: MarketLevelPeriod, ticker: string, dates: string[] = [], maxRows?: number, source?: string): Promise<MarketLevelDisplayRow[]> {
    const normalizedTicker = ticker.trim().toUpperCase();
    const collectionName = period === 'daily' ? 'marketTickerLevelsDaily' : 'marketTickerLevelsWeekly';
    const base = collection(this.db, collectionName, normalizedTicker, 'records');

    if (dates.length) {
      // Batch via an 'in' query on document ID instead of one getDoc() per date —
      // a getDoc() bills a read even for a missing document (e.g. a probed date
      // with no data yet), while an 'in' query only bills for docs that exist.
      // Firestore's 'in' operator caps at 30 values, so chunk defensively.
      const ids = dates.map(dateValue => source ? `${source}_${dateValue}` : dateValue);
      const chunks: string[][] = [];
      for (let i = 0; i < ids.length; i += 30) chunks.push(ids.slice(i, i + 30));

      const snaps = await Promise.all(
        chunks.map(chunk => getDocs(query(base, where(documentId(), 'in', chunk))))
      );
      return snaps
        .flatMap(snap => snap.docs)
        .map(docSnap => this.toDisplayRow(docSnap.data(), normalizedTicker, docSnap.id))
        .sort((a, b) => b.date.localeCompare(a.date));
    }

    // No maxRows given — cap it ourselves so callers like exportAllLevelsCsv()
    // can't trigger an unbounded read of a ticker's entire stored history.
    const effectiveMaxRows = maxRows ?? 500;
    const q = source
      ? query(base, where('source', '==', source), limit(effectiveMaxRows))
      : query(base, orderBy('date', 'desc'), limit(effectiveMaxRows));
    const snap = await getDocs(q);

    const rows = snap.docs
      .map(docSnap => this.toDisplayRow(docSnap.data(), normalizedTicker, docSnap.id))
      .filter(row => source || !row.source)
      .sort((a, b) => b.date.localeCompare(a.date));

    return maxRows ? rows.slice(0, maxRows) : rows;
  }

  private tickerOrder(ticker: string): number {
    const index = TICKER_ORDER.indexOf(ticker);
    return index === -1 ? TICKER_ORDER.length : index;
  }

  private toDisplayRow(data: Record<string, any>, ticker: string, fallbackDate: string): MarketLevelDisplayRow {
    return {
      ticker: data['ticker'] ?? ticker,
      close: data['close'] ?? 0,
      iv: data['iv'] ?? 0,
      expectedMove: data['expectedMove'] ?? 0,
      s1: data['s1'],
      pivot: data['pivot'],
      r1: data['r1'],
      s2: data['s2'],
      r2: data['r2'],
      date: data['date'] ?? fallbackDate,
      source: data['source'],
      updatedAt: this.toDate(data['updatedAt']),
    };
  }

  private toDate(value: unknown): Date | null {
    if (value instanceof Timestamp) return value.toDate();
    if (value instanceof Date) return value;
    if (typeof value === 'number') return new Date(value);
    if (value && typeof (value as { toDate?: unknown }).toDate === 'function') {
      return (value as { toDate: () => Date }).toDate();
    }
    return null;
  }
}
